import { computed, action, observable } from "mobx"
import { uuid } from "../../light/utils"

import {
  SquareOrder,
  postSquareProxy,
  SquareMoney,
  SquareOrderResponse,
  SquareTransactionRequest,
  SquareTransactionResponse
} from "../../light/light-square"
import { LightModel, LightObject, BookingDB } from "./BookingLightDB"
import { LineItem } from "./LineItem"
import { SessionGroup, $SessionGroup } from "./SessionGroup"
import { Member, $Member } from "./Member"

export interface $Order extends LightObject {
  $lineItems: { [id: string]: string }
  $sessionGroups: { [$id: string]: string }
  $member: string | null
  $reference: string
  $referenceType: string
  sqLocationId: string
  sqOrderId: string | null
  submittedDateString: string | null
}

export class Order implements LightModel {
  constructor(
    db: BookingDB,
    $reference: string,
    $referenceType: string,
    sqLocationId: string
  ) {
    this.db = db
    this.object = Order.create$Object($reference, $referenceType, sqLocationId)
  }

  static create$Object(
    $reference: string,
    $referenceType: string,
    sqLocationId: string
  ): $Order {
    return {
      $id: uuid(),
      $type: "order",
      $version: uuid(),
      $parentVersion: null,
      $versionDateString: new Date().toISOString(),
      $member: null,
      $lineItems: {},
      $sessionGroups: {},
      $reference: $reference,
      $referenceType: $referenceType,
      sqLocationId: sqLocationId,
      sqOrderId: null,
      submittedDateString: null
    }
  }

  @computed
  get id() {
    return this.object.$id
  }

  @computed
  get sessionGroups(): SessionGroup[] {
    if (this.object.$sessionGroups)
      return Object.keys(this.object.$sessionGroups).map(k => {
        const object = this.db.file.objects[k] as $SessionGroup
        return new SessionGroup(this.db, object)
      })
    return []
  }

  @computed
  get displayDate() {
    return this.sessionGroups[0].session.date.format("ddd MMM DD, YYYY")
  }

  @action.bound
  addSessionGroup(sessionGroup: SessionGroup) {
    this.object.$sessionGroups[sessionGroup.object.$id] =
      sessionGroup.object.$id
  }

  @computed
  get type() {
    return this.object.$type
  }

  @computed
  get children() {
    let children: LightModel[] = this.lineItems
    children = children.concat(this.sessionGroups)
    if (this.member) children.push(this.member)
    return children
  }

  isValid = true
  invalidMessage = undefined
  clean() {}

  db: BookingDB
  @observable
  object: $Order

  @computed
  get lineItemsForSessionGroups() {
    const memberAttendance: { [name: string]: number } = {}

    const lineItems: LineItem[] = []
    this.sessionGroups.forEach(s => {
      const sessionType = s.session.sessionType

      const members = s.members
      members &&
        members.forEach(m => {
          const isObserving = sessionType.object.name === "Observe Mo"
          const attendanceId = `${sessionType.id}-${m.id}`
          if (isObserving) {
          } else
            memberAttendance[attendanceId] =
              memberAttendance[attendanceId] >= 0
                ? memberAttendance[attendanceId] + 1
                : 0

          const shouldUseFirstPass = memberAttendance[attendanceId] === 0
          const catalogObjectId = shouldUseFirstPass
            ? sessionType.object.firstPassCatalogItemId
            : sessionType.object.secondPassCatalogItemId

          const totalInCents = shouldUseFirstPass
            ? sessionType.object.firstPassPriceInCents
            : sessionType.object.secondPassPriceInCents
          let fullName = m.fullName ? m.fullName : ""
          let passNumber =
            shouldUseFirstPass && !isObserving
              ? "1st Pass"
              : !isObserving
              ? "2nd Pass"
              : null
          let sessionTime = `${s.session.timeSlot} - ${s.session.endTimeString}`
          let sessionTypeName = sessionType.object.name
          let baseNote = `${sessionTypeName} ${sessionTime} ${fullName}`
          let note = !isObserving ? baseNote + " " + passNumber : baseNote

          const lineItem = new LineItem(this.db)
          lineItem.object.fullName = fullName
          lineItem.object.passNumber = passNumber
          lineItem.object.sessionTypeName = sessionTypeName
          lineItem.object.sessionTime = sessionTime
          lineItem.object.note = note
          lineItem.object.quantity = 1
          lineItem.object.priceInCents = totalInCents
          lineItem.object.catalogObjectId = catalogObjectId
          lineItems.push(lineItem)
        })
    })
    return lineItems
  }

  @computed
  get lineItems(): LineItem[] {
    return this.lineItemsForSessionGroups
  }

  @computed
  get subtotalInCents() {
    return (
      this.lineItems &&
      this.lineItems.reduce((acc, l) => (acc += l.totalInCents), 0)
    )
  }

  @computed
  get totalInCents() {
    return this.subtotalInCents + this.subtotalInCents * 0.08
  }

  @computed
  get totalFriendly() {
    return (this.totalInCents / 100).toFixed(2)
  }

  @computed
  get taxInCents() {
    return this.subtotalInCents * 0.08
  }

  @computed
  get taxFriendly() {
    return (this.taxInCents / 100).toFixed(2)
  }

  @computed
  get isComplete(): boolean {
    return this.object.submittedDateString != null
  }

  @computed
  get toSquareOrder(): SquareOrder | null {
    if (this.object.$reference)
      return {
        idempotency_key: uuid(),
        reference_id: this.object.$reference,
        line_items: this.lineItems.map(l => l.toSquare)
      }
    return null
  }

  @computed
  get member(): Member | null {
    if (this.object.$member)
      return new Member(this.db, this.db.file.objects[
        this.object.$member
      ] as $Member)
    return null
  }

  createSquareTransaction(
    sqOrder: SquareOrderResponse,
    cardNonce: string,
    note: string
  ): SquareTransactionRequest {
    if (!this.member) throw "Missing member on order"

    if (!sqOrder || !this.object.sqOrderId)
      throw "Unable to create square order in Order"

    const sqCustomer = this.member.toSquareCustomer
    const totalOrderMoney = sqOrder.total_money as SquareMoney

    const t = {
      idempotency_key: uuid(),
      order_id: sqOrder.id,
      customer_id: this.member.id,
      amount_money: totalOrderMoney,
      card_nonce: cardNonce,
      billing_address: sqCustomer.address,
      reference_id: this.id,
      note: note,
      delay_capture: false
    }
    return t
  }

  transaction: SquareTransactionResponse | null = null

  @action.bound
  async pay(cardNonce: string, note: string) {
    if (!this.member)
      throw new Error("Must select payingMember/owner of booking before paying")

    this.saveSessions()

    let sqCustomer
    if (this.member.object.sqCustId) {
      sqCustomer = await postSquareProxy(
        `/customers/${this.member.object.sqCustId}`,
        null,
        "get"
      )
    } else
      sqCustomer = await postSquareProxy(
        "/customers",
        this.member.toSquareCustomer
      )

    sqCustomer = sqCustomer.customer
    if (sqCustomer) {
      let sqOrder: SquareOrderResponse = await postSquareProxy(
        `/locations/${this.object.sqLocationId}/orders`,
        this.toSquareOrder
      )
      sqOrder = (sqOrder as any).order
      this.object.sqOrderId = sqOrder.id
      if (sqOrder) {
        const sqTransaction: SquareTransactionResponse = await postSquareProxy(
          `/locations/${this.object.sqLocationId}/transactions`,
          this.createSquareTransaction(sqOrder, cardNonce, note)
        )
        this.transaction = sqTransaction
        this.object.submittedDateString = new Date().toISOString()
        this.member.object.sqCustId = sqCustomer.id
      }
    }
  }

  @action.bound
  saveSessions() {
    this.sessionGroups.forEach(s => {
      s.session.addSessionGroup(s)
      this.db.save(s.session)
    })
  }
}
